import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useLayoutEffect, useState, useRef, useMemo } from 'react';
import ThemeToggle from '../components/ThemeToggle';
import Logo from '../components/Logo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { FiUser, FiMenu, FiLogOut, FiSettings, FiGrid, FiMessageSquare } from 'react-icons/fi';
// Removed unused imports after simplifying profile link
import NotificationBell from '../components/NotificationBell';
import MessagesBadgeButton from '../components/MessagesBadgeButton';
import { useTranslation } from 'react-i18next';
import ErrorBoundary from '../components/ErrorBoundary';
import MotionWrapper from '../components/MotionWrapper';
import AIAssistant from '../components/AIAssistant';
import EnvBadge from '../components/EnvBadge.jsx';
import DatabaseModeToggle from '../components/DatabaseModeToggle.jsx';
import CurrencySelector from '../components/CurrencySelector.jsx';
import { DemoBanner } from '../components/DemoLoginCards.jsx';
import { refreshApiBaseDetection, isOnDemoSite } from '../lib/api.js';

export default function RootLayout() {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const prevHtmlOverflow = useRef('');

  const [scrolled, setScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [userCardMin] = useState(() => {
    try { return localStorage.getItem('ui:userCard:min') === '1'; } catch { return false; }
  });
  const userMenuRef = useRef(null);
  const navigate = useNavigate();
  const prevBodyOverflow = useRef('');
  const mainRef = useRef(null);
  const footerRef = useRef(null);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isLogin = location.pathname === '/login' || location.pathname.startsWith('/login');
  const isRegister = location.pathname === '/register' || location.pathname.startsWith('/register');
  const isForgot = location.pathname === '/forgot-password';
  const isAuth = isLogin || isRegister || isForgot;
  
  // Redirect logged-in users away from auth pages (login/register/forgot-password) to dashboard
  // Uses useLayoutEffect for synchronous check before paint (prevents flash of login page)
  useLayoutEffect(() => {
    if (!isAuth) return;
    let hasToken = false;
    try {
      // Check localStorage directly (most reliable on initial mount)
      hasToken = Boolean(localStorage.getItem('auth:token'));
    } catch (e) { void e; }
    if (hasToken) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuth, navigate]);
  
  // Also check when auth state updates (handles login completion)
  useEffect(() => {
    if (!isAuth) return;
    if (authToken || authUser) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuth, authToken, authUser, navigate]);
  // Lock background scroll when off-canvas menu is open
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const mainEl = mainRef.current;
    const footerEl = footerRef.current;
    if (menuOpen) {
      prevHtmlOverflow.current = html.style.overflow;
      prevBodyOverflow.current = body.style.overflow;
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      // Disable interactions on underlying content
      mainEl?.setAttribute('inert', '');
      footerEl?.setAttribute('inert', '');
    } else {
      html.style.overflow = prevHtmlOverflow.current || '';
      body.style.overflow = prevBodyOverflow.current || '';
      mainEl?.removeAttribute('inert');
      footerEl?.removeAttribute('inert');
    }
    return () => {
      html.style.overflow = '';
      body.style.overflow = '';
      mainEl?.removeAttribute('inert');
      footerEl?.removeAttribute('inert');
    };
  }, [menuOpen]);
  // Header background: transparent at top, adds background after scrolling
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 2);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);
  useEffect(() => {
    const readAuth = () => {
      let user = null; let token = null;
      try { const raw = localStorage.getItem('auth:user'); if (raw) user = JSON.parse(raw); } catch (e) { void e; }
      try { token = localStorage.getItem('auth:token') || null; } catch (e) { void e; }
      setAuthUser(user || null);
      setAuthToken(token || null);
      // Align API base with the currently stored token on app load
      try { if (token) refreshApiBaseDetection(); } catch (e) { void e; }
    };
    readAuth();
    const onStorage = (e) => { if (!e || !e.key) return; if (e.key.startsWith('auth:')) readAuth(); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  // Query param-based UI reset to harmonize behavior across browser profiles
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      const shouldReset = params.get('resetUi') === '1';
      if (shouldReset) {
        try {
          localStorage.removeItem('theme');
          localStorage.removeItem('i18nextLng');
          localStorage.removeItem('auth:token');
          localStorage.removeItem('auth:user');
          localStorage.removeItem('ui:userCard:min');
        } catch (e) { void e; }
        const next = new URL(window.location.href);
        next.searchParams.delete('resetUi');
        window.location.replace(next.toString());
      }
    } catch (e) { void e; }
  }, [location.search]);
  // Global event to open AI chat panel from anywhere
  useEffect(() => {
    const handler = () => setChatOpen(true);
    window.addEventListener('kreativ:openAiChat', handler);
    return () => window.removeEventListener('kreativ:openAiChat', handler);
  }, []);
  // Global AI-driven navigation (from voice/command in AI chat)
  useEffect(() => {
    function onNavigate(e) {
      const { path, search } = e.detail || {};
      try {
        if (path) {
          navigate({ pathname: path, search: search || '' });
          setChatOpen(false);
        }
      } catch (e) { void e; }
    }
    window.addEventListener('kreativ:navigate', onNavigate);
    return () => window.removeEventListener('kreativ:navigate', onNavigate);
  }, [navigate]);
  // Persist minimized state for user info card
  useEffect(() => {
    try { localStorage.setItem('ui:userCard:min', userCardMin ? '1' : '0'); } catch (e) { void e; }
  }, [userCardMin]);
  useEffect(() => {
    const onDocClick = (e) => {
      if (!userMenuOpen) return;
      const el = userMenuRef.current;
      if (el && !el.contains(e.target)) setUserMenuOpen(false);
    };
    const onEsc = (e) => { if (userMenuOpen && e.key === 'Escape') setUserMenuOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [userMenuOpen]);
  // Normalize roles and compute persona for AI assistant
  const roles = useMemo(() => {
    const raw = Array.isArray(authUser?.roles) ? authUser.roles : (authUser?.role ? [authUser.role] : []);
    const map = (r) => {
      // Handle role objects like {key: 'admin', name: 'Administrator'}
      const k = typeof r === 'object' && r !== null 
        ? String(r.key || r.name || r.role || '').trim().toLowerCase()
        : String(r || '').trim().toLowerCase();
      if (k === 'client') return 'client';
      if (k === 'creative' || k === 'creator' || k === 'artist') return 'creative';
      return k;
    };
    return raw.map(map);
  }, [authUser]);
  const isAdmin = roles.includes('admin');
  const isClient = roles.includes('client') || isAdmin;
  const isCreative = roles.includes('creative') || isAdmin;
  const persona = useMemo(() => {
    const loggedIn = !!(authToken || authUser);
    if (!loggedIn) return 'client';
    if (isClient && isCreative) {
      if (location.pathname.includes('/dashboard/creative')) return 'creative';
      if (location.pathname.includes('/dashboard/client')) return 'client';
      return 'client';
    }
    if (isCreative) return 'creative';
    return 'client';
  }, [authToken, authUser, isClient, isCreative, location.pathname]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const loggedIn = Boolean(
    authToken || authUser || (typeof localStorage !== 'undefined' && localStorage.getItem('auth:token'))
  );
  const suggestionItems = useMemo(() => {
    const path = location.pathname || '/';
    // Page-aware suggestions for anonymous (client persona)
    if (!loggedIn || persona === 'client') {
      if (path === '/' || path.startsWith('/home')) {
        return [
          { label: 'Browse services', action: () => navigate('/services') },
          { label: 'Browse creatives', action: () => navigate('/creatives') },
          { label: 'Find nearby creatives', action: () => navigate('/creatives/map') },
          { label: 'Ask AI to match creatives', action: () => setChatOpen(true) },
        ];
      }
      if (path.startsWith('/services')) {
        return [
          { label: 'Explore services', action: () => navigate('/services') },
          { label: 'Find creatives offering Logo Design', action: () => navigate('/services/allservices/q/logo-design') },
          { label: 'Ask AI for service recommendations', action: () => setChatOpen(true) },
        ];
      }
      if (path.startsWith('/creatives/map')) {
        return [
          { label: 'Browse creatives list', action: () => navigate('/creatives') },
          { label: 'Filter by city', action: () => navigate('/creatives/map?city=') },
          { label: 'Ask AI to find a nearby designer', action: () => setChatOpen(true) },
        ];
      }
      if (path.startsWith('/creatives')) {
        return [
          { label: 'Open creatives map', action: () => navigate('/creatives/map') },
          { label: 'Filter by skill', action: () => navigate('/creatives?q=designer') },
          { label: 'Ask AI to suggest creatives', action: () => setChatOpen(true) },
        ];
      }
      if (path.startsWith('/countries') || path.startsWith('/cities')) {
        return [
          { label: 'See creatives in a city', action: () => navigate('/creatives?city=') },
          { label: 'Open creatives map', action: () => navigate('/creatives/map') },
          { label: 'Ask AI to find local creatives', action: () => setChatOpen(true) },
        ];
      }
      // Generic default elsewhere
      return [
        { label: 'Browse services', action: () => navigate('/services') },
        { label: 'Browse creatives', action: () => navigate('/creatives') },
        { label: 'Ask AI to match creatives', action: () => setChatOpen(true) },
      ];
    }
    // Talent suggestions when logged in (page-aware baseline)
    if (path.startsWith('/dashboard')) {
      return [
        { label: 'View tasks', action: () => navigate('/dashboard/tasks') },
        { label: 'Update portfolio', action: () => navigate('/dashboard/creative') },
        { label: 'Ask AI for tips', action: () => setChatOpen(true) },
      ];
    }
    return [
      { label: 'Open dashboard', action: () => navigate('/dashboard') },
      { label: 'Update portfolio', action: () => navigate('/dashboard/creative') },
      { label: 'Ask AI for tips', action: () => setChatOpen(true) },
    ];
  }, [persona, loggedIn, navigate, location.pathname]);
  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-black text-zinc-900 dark:text-white">
      <header className={[
        'sticky top-0 z-50 app-header',
        scrolled
          ? 'header--scrolled border-b border-zinc-300 bg-white backdrop-blur dark:border-zinc-800 dark:bg-black'
          : 'header--transparent bg-transparent border-transparent'
      ].join(' ')}>
        <div className={`container flex items-center ${isHome ? 'justify-end' : 'justify-between'} py-3`}>
          {!isHome && (
            <Link to="/" className="flex items-center gap-2 font-semibold text-zinc-900 dark:text-white">
              <Logo className="h-6 w-auto" alt="Kreatives" />
              {/* brand title removed */}
            </Link>
          )}
          <nav className="relative flex items-center gap-3 text-sm">
            {/* Icon bar only (links removed) */}
            {isAdmin && <DatabaseModeToggle isAdmin={isAdmin} />}
            <LanguageSwitcher compact dense />
            <ThemeToggle compact dense />
            {loggedIn && (
              <>
                <MessagesBadgeButton />
                <NotificationBell />
              </>
            )}
            <button
              className="icon-btn w-10 h-10"
              aria-label="Profile"
              aria-haspopup="menu"
              aria-expanded={userMenuOpen ? true : false}
              onClick={() => {
                // Check both React state AND localStorage for auth status
                let isLoggedIn = !!(authToken || authUser);
                if (!isLoggedIn) {
                  try {
                    isLoggedIn = Boolean(localStorage.getItem('auth:token'));
                  } catch (e) { void e; }
                }
                if (!isLoggedIn) { navigate('/login'); return; }
                setUserMenuOpen((o) => !o);
              }}
            >
              <FiUser className="w-5 h-5" />
            </button>
            {userMenuOpen && loggedIn && (
              <div
                ref={userMenuRef}
                className="absolute right-0 top-full mt-2 w-64 bg-card-bg border border-card-border rounded-lg shadow-xl p-3 z-50"
                role="menu"
                aria-label="User menu"
              >
                <div className="flex items-center gap-2 pb-3 border-b border-card-border">
                  <div className="w-9 h-9 rounded-full bg-subtle-bg border border-card-border flex items-center justify-center">
                    <FiUser className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-primary-text truncate">{authUser?.display_name || authUser?.name || 'User'}</div>
                    {authUser?.email && (
                      <div className="text-xs text-secondary-text truncate">{authUser.email}</div>
                    )}
                  </div>
                </div>
                <div className="pt-3 space-y-1">
                  <Link to="/dashboard" className="flex items-center gap-2 px-2 py-1 rounded hover:bg-subtle-bg text-primary-text" role="menuitem">
                    <FiGrid className="w-4 h-4" />
                    <span>Dashboard</span>
                  </Link>
                  <Link to="/dashboard/profile" className="flex items-center gap-2 px-2 py-1 rounded hover:bg-subtle-bg text-primary-text" role="menuitem">
                    <FiUser className="w-4 h-4" />
                    <span>Profile</span>
                  </Link>
                  <Link to="/dashboard/settings" className="flex items-center gap-2 px-2 py-1 rounded hover:bg-subtle-bg text-primary-text" role="menuitem">
                    <FiSettings className="w-4 h-4" />
                    <span>Settings</span>
                  </Link>
                </div>
                <div className="pt-2 mt-2 border-t border-card-border">
                  <button
                    className="w-full flex items-center gap-2 px-2 py-2 rounded hover:bg-subtle-bg text-primary-text"
                    role="menuitem"
                    onClick={() => {
                      try { localStorage.removeItem('auth:token'); localStorage.removeItem('auth:user'); } catch (e) { void e; }
                      setAuthToken(null);
                      setAuthUser(null);
                      setUserMenuOpen(false);
                      navigate('/login');
                    }}
                  >
                    <FiLogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
            <button
              className="icon-btn w-10 h-10"
              aria-label="Open menu"
              onClick={() => setMenuOpen(true)}
            >
              <FiMenu className="w-5 h-5" />
            </button>
          </nav>
        </div>
      </header>

      <main ref={mainRef} className="min-h-screen bg-white dark:bg-black" aria-hidden={menuOpen ? true : undefined}>
        <div className={`${isAuth ? '' : 'container pt-6'} ${!isHome && !isAuth ? 'pb-16' : ''}`}>
          <ErrorBoundary>
            {location.pathname.startsWith('/dashboard') ? (
              // Skip global route transition for dashboard pages; handled within DashboardLayout
              <Outlet />
            ) : (
              <MotionWrapper key={location.pathname}>
                <Outlet />
              </MotionWrapper>
            )}
          </ErrorBoundary>
        </div>
      </main>

      <footer ref={footerRef} className="border-t border-zinc-300 bg-white text-black dark:border-zinc-800 dark:bg-black dark:text-white" aria-hidden={menuOpen ? true : undefined}>
        <div className="container py-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-sm md:divide-x md:divide-zinc-300 dark:md:divide-zinc-600">
            {/* Brand */}
            <div className="flex items-center">
              <Logo className="h-16 md:h-20 w-auto" alt="Kreatives" variant="footer" />
            </div>
            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-3">{t('nav.home')} &amp; {t('nav.cities')}</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="nav-link">{t('nav.home')}</Link></li>
                <li><Link to="/cities" className="nav-link">{t('nav.cities')}</Link></li>
                <li><Link to="/services" className="nav-link">{t('nav.services')}</Link></li>
                <li><Link to="/creatives" className="nav-link">{t('nav.talents')}</Link></li>
                <li><Link to="/events" className="nav-link">Events</Link></li>
              </ul>
            </div>
            {/* Important Links */}
            <div>
              <h4 className="font-semibold mb-3">{t('nav.terms')} &amp; {t('nav.privacy')}</h4>
              <ul className="space-y-2">
                <li><Link to="/terms" className="nav-link">{t('nav.terms')}</Link></li>
                <li><Link to="/privacy" className="nav-link">{t('nav.privacy')}</Link></li>
                <li><Link to="/contact" className="nav-link">{t('footer.contact')}</Link></li>
              </ul>
            </div>
            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-3">{t('footer.contact')}</h4>
              <div className="space-y-1 text-black dark:text-white">
                <div>Hans Michelsensgatan 9, 211 20 Malmö</div>
                <div>Email: <a href="mailto:info@kreatives.app" className="nav-link">info@kreatives.app</a></div>
              </div>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-zinc-300 dark:border-zinc-600 text-center text-xs text-black dark:text-white">© {new Date().getFullYear()} Kreatives. All rights reserved.</div>
        </div>
      </footer>
      {/* Floating user info card removed per requirements */}
      
      {/* AI Chat launcher and Suggestions - HIDDEN FOR LAUNCH
      <button
        className="fixed right-4 bottom-24 z-60 w-12 h-12 rounded-full shadow-md icon-btn"
        aria-label="Open AI Chat"
        title="AI Chat"
        onClick={() => setChatOpen(true)}
      >
        <FiMessageSquare className="w-6 h-6" />
      </button>
      {showSuggestions && (
        <div className="fixed right-4 bottom-[140px] z-60 w-[240px] bg-card-bg border border-card-border rounded-xl shadow-md p-2 card-hover">
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs font-medium text-primary-text">Quick suggestions</div>
            <button className="icon-btn w-6 h-6" aria-label="Dismiss suggestions" onClick={() => setShowSuggestions(false)}>✕</button>
          </div>
          <div className="flex flex-col gap-2">
            {suggestionItems.map((s, idx) => (
              <button key={idx} className="btn btn-outline text-xs" onClick={s.action}>{s.label}</button>
            ))}
          </div>
        </div>
      )}
      */}
      {/* AI Chat off-canvas panel */}
      {chatOpen && (
        <div className="fixed inset-0 z-100">
          {/* Backdrop */}
          <button
            className="absolute inset-0 bg-black/30 dark:bg-white/10 backdrop-blur-sm"
            aria-label="Close chat"
            onClick={() => setChatOpen(false)}
          />
          {/* Panel */}
          <aside className="absolute right-0 top-0 h-screen w-full sm:w-[420px] md:w-[500px] bg-white dark:bg-black text-zinc-900 dark:text-white border-l border-zinc-200 dark:border-zinc-800 shadow-xl p-4 flex flex-col overflow-y-auto offcanvas-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#1756fd] text-white flex items-center justify-center">🤖</div>
                <div className="font-semibold text-primary-text">AI Chat</div>
              </div>
              <button className="icon-btn w-10 h-10" aria-label="Close" title="Close" onClick={() => setChatOpen(false)}>✕</button>
            </div>
            <AIAssistant
              title="AI Assistant"
              persona={persona}
              pageName={location.pathname}
              shortReplyPolicy
              loggedIn={loggedIn}
              userId={authUser?.id || authUser?.email || authUser?.name || null}
            />
          </aside>
        </div>
      )}
      {/* Off-canvas panel rendered at root to cover full viewport */}
      {menuOpen && (
        <div className="fixed inset-0 z-100">
          {/* Backdrop (slight dim, theme-aware) */}
          <button
            className="absolute inset-0 bg-black/30 dark:bg.white/10 backdrop-blur-sm"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
          />
          {/* Panel (match old-app: full-width on mobile, 500px on desktop) */}
          <aside className="absolute right-0 top-0 h-screen w-full sm:w-[420px] md:w-[500px] bg-white dark:bg-black text-zinc-900 dark:text-white border-l border-zinc-200 dark:border-zinc-800 shadow-xl p-4 flex flex-col overflow-y-auto offcanvas-panel">
            <div className="relative mb-4">
              <button
                className="icon-btn w-10 h-10 absolute top-4 right-4"
                aria-label="Close"
                onClick={() => setMenuOpen(false)}
                title="Close"
              >
                ✕
              </button>
              <div className="flex items-center justify-center">
                <Logo className="h-[150px] w-auto" alt="Kreatives" />
              </div>
            </div>
            <div className="space-y-3 text-sm text-left">              
              <NavLink to="/creatives/map" onClick={() => setMenuOpen(false)} className={({ isActive }) => ['nav-link', 'block w-full', 'font-bold', 'text-xl', isActive ? 'nav-link-active' : ''].join(' ')}>{t('nav.talentMap')}</NavLink>
              <NavLink to="/" onClick={() => setMenuOpen(false)} className={({ isActive }) => ['nav-link', 'block w-full', 'font-bold', 'text-xl', isActive ? 'nav-link-active' : ''].join(' ')}>{t('nav.home')}</NavLink>
              <NavLink to="/countries" onClick={() => setMenuOpen(false)} className={({ isActive }) => ['nav-link', 'block w.full', 'font-bold', 'text-xl', isActive ? 'nav-link-active' : ''].join(' ')}>{t('nav.countries')}</NavLink>
              <NavLink to="/services" onClick={() => setMenuOpen(false)} className={({ isActive }) => ['nav-link', 'block w-full', 'font-bold', 'text-xl', isActive ? 'nav-link-active' : ''].join(' ')}>{t('nav.services')}</NavLink>
              <NavLink to="/creatives" end onClick={() => setMenuOpen(false)} className={({ isActive }) => ['nav-link', 'block w.full', 'font-bold', 'text-xl', isActive ? 'nav-link-active' : ''].join(' ')}>{t('nav.talents')}</NavLink>
              <NavLink to="/how-it-works" onClick={() => setMenuOpen(false)} className={({ isActive }) => ['nav-link', 'block w.full', 'font-bold', 'text-xl', isActive ? 'nav-link-active' : ''].join(' ')}>{t('nav.howItWorks')}</NavLink>
              <NavLink to="/about" onClick={() => setMenuOpen(false)} className={({ isActive }) => ['nav-link', 'block w.full', 'font-bold', 'text-xl', isActive ? 'nav-link-active' : ''].join(' ')}>{t('nav.about')}</NavLink>
              <NavLink to="/events" onClick={() => setMenuOpen(false)} className={({ isActive }) => ['nav-link', 'block w-full', 'font-bold', 'text-xl', isActive ? 'nav-link-active' : ''].join(' ')}>Events</NavLink>
            </div>
            <div className="mt-5 flex items-center gap-3">
              <CurrencySelector compact dropUp />
              <LanguageSwitcher compact usePortal placement="top" />
              <ThemeToggle compact />
              <Link
                to="/contact"
                onClick={() => setMenuOpen(false)}
                className="cta-brand inline-flex items-center gap-2 px-3 py-3 justify-center flex-1"
                data-variant="primary"
                aria-label="Contact"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-5 h-5"
                  aria-hidden="true"
                >
                  <path d="M3 7.5l9 6 9-6" />
                  <rect x="3" y="6" width="18" height="12" rx="2" ry="2" />
                </svg>
                <span>{t('actions.contact')}</span>
              </Link>
            </div>
            <div className="mt-3">
              <Link
                to="/login"
                onClick={() => setMenuOpen(false)}
                className="cta-brand w-full py-3 inline-flex items.center gap-2 justify-center"
                data-variant="primary"
                aria-label="Login"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-5 h-5"
                  aria-hidden="true"
                >
                  <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4" />
                  <path d="M10 17l5-5-5-5" />
                  <path d="M15 12H3" />
                </svg>
                <span>{t('actions.login')}</span>
              </Link>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
